import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ExhibitorPairStatus, IExhibitorPair } from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import getNextPageParam from "tools/pagination/getNextPageParam";
import useInvalidateOnShowNotifications from "hooks/useInvalidateOnShowNotifications";
import {
  EXHIBITOR_PAIR_REALTIME_KEYS,
  EXHIBITOR_PAIRS_PAGE_SIZE,
  exhibitorPairsQueryKey,
} from "./exhibitorPairQueries";

type UseInfiniteExhibitorPairsArgs = {
  showId?: string;
  search?: string;
  status?: ExhibitorPairStatus;
  enabled?: boolean;
};

type UseInfiniteExhibitorPairsResult = {
  pairs: IExhibitorPair[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
};

const useInfiniteExhibitorPairs = ({
  showId,
  search = "",
  status,
  enabled = true,
}: UseInfiniteExhibitorPairsArgs): UseInfiniteExhibitorPairsResult => {
  const term = search.trim();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [...exhibitorPairsQueryKey(showId), "list", status ?? null, term],
    queryFn: ({ pageParam = 1 }) =>
      apiV2.exhibitor.pair.getPaginated({
        showId: showId as string,
        page: pageParam,
        limit: EXHIBITOR_PAIRS_PAGE_SIZE,
        status,
        search: term || undefined,
      }),
    getNextPageParam,
    enabled: enabled && !!showId,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  useInvalidateOnShowNotifications({
    showId: enabled ? showId : undefined,
    keys: EXHIBITOR_PAIR_REALTIME_KEYS,
    queryKey: exhibitorPairsQueryKey(showId),
  });

  const pairs = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [
    data,
  ]);

  return {
    pairs,
    fetchNextPage: () => {
      fetchNextPage();
    },
    hasNextPage: !!hasNextPage,
    isFetching,
    isFetchingNextPage,
  };
};

export default useInfiniteExhibitorPairs;
